import React from 'react'
import { ReactReduxContext } from 'react-redux'
import { Modal, ModalBody } from 'reactstrap'
import { Circulation, IANSReference } from '~/types'
import { filterWebsites } from '../CirculateTab/CirculateTab.utils'
import { useSettings } from '../SettingsContext'

interface SelectorModalProps {
  isTemplate: boolean
  isOpen: boolean
  toggle: (value: boolean) => void
  handleOnMssData: (selectedCirculationData: any) => void
}

const CANCEL_SELECTOR = 'cancelSectionSelector'
const INIT_DATA = 'sendSelectorInitData'
const SEND_DATA = 'selectorANSData'

export const SelectorModal: React.FC<SelectorModalProps> = ({
  isTemplate,
  isOpen,
  toggle,
  handleOnMssData,
}) => {
  const settings = useSettings()

  const iframeRef = React.useRef<HTMLIFrameElement>(null)
  const onDataRef = React.useRef(handleOnMssData)

  React.useEffect(() => {
    onDataRef.current = handleOnMssData
  }, [])

  const { store } = React.useContext(ReactReduxContext)
  const { storyData, websites } = store.getState()

  function formatWebsites() {
    const circWebsites = {}

    storyData.circulations.forEach((circulation: Circulation) => {
      circWebsites[`${circulation.website_id}`] = {
        website_section: {
          referent: {
            id: circulation.website_primary_section?.referent.id,
            type: circulation.website_primary_section?.referent.type,
            website: circulation.website_primary_section?.referent.website,
          },
          type: 'reference',
        },
        website_url: circulation.website_url,
      }
    })
    return circWebsites
  }

  function formatSections() {
    const sections: IANSReference[] = []

    storyData.circulations.forEach((circulation: Circulation) => {
      if (circulation.website_sections) {
        circulation.website_sections.forEach((circ) => {
          sections.push(circ)
        })
      }
    })
    return sections
  }

  function formatFilteredSites() {
    const filteredSites = filterWebsites(
      websites,
      window.userRolesAuth.story,
      settings.siteServiceHierarchy
    )

    return filteredSites
  }

  React.useEffect(() => {
    const initSelectorData = () => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          {
            message: 'selectorInitData',
            action: 'circulate',
            data: {
              published: !isTemplate ? storyData.isPublished : null,
              canonicalWebsite: storyData.story?.canonical_website,
              contentTitle: storyData.story.headlines.basic,
              taxonomy: {
                sections: formatSections(),
              },
              websites: formatWebsites(),
              filteredSites: formatFilteredSites(),
            },
          },
          window.location.origin
        )
      }
    }

    const handleMessage = (event) => {
      if (
        !iframeRef.current ||
        !iframeRef.current.contentWindow ||
        iframeRef.current.contentWindow !== event.source
      ) {
        return
      }

      const { message, data } = event.data

      if (message === 'cancelSectionSelector') {
        toggle(false)
        return
      }

      switch (message) {
        case CANCEL_SELECTOR:
          toggle(false)
          break
        case INIT_DATA:
          initSelectorData()
          break
        case SEND_DATA:
          onDataRef.current(data)
          break
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [websites, storyData.circulations, storyData.isPublished])

  return (
    <Modal
      wrapClassName="site-selector-v2--modal"
      size="lg"
      centered
      isOpen={isOpen}
      toggle={() => toggle(true)}
    >
      <ModalBody>
        <iframe
          ref={iframeRef}
          title="Sites & Sections Selector"
          height="100%"
          width="100%"
          src="/section-selector/v2"
        />
      </ModalBody>
    </Modal>
  )
}
